import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { SkeletonRegion, fitCount, useElementSize, useStableKeys } from './_shared'

export interface LineSkeletonProps {
	className?: string
	label?: string | false
}

const TICK_SLOT_WIDTH = 56
// Below this the axis ticks crowd out the plot itself.
const MIN_HEIGHT_FOR_TICKS = 120

const POINTS = [
	{ id: 'pt-1', x: 50, y: 120 },
	{ id: 'pt-2', x: 100, y: 60 },
	{ id: 'pt-3', x: 150, y: 125 },
	{ id: 'pt-4', x: 200, y: 120 },
	{ id: 'pt-5', x: 250, y: 100 },
	{ id: 'pt-6', x: 300, y: 170 },
	{ id: 'pt-7', x: 350, y: 140 },
	{ id: 'pt-8', x: 400, y: 80 },
	{ id: 'pt-9', x: 450, y: 30 },
]

const PATH_D = POINTS.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')

export function LineSkeleton({ className, label = 'Loading chart' }: LineSkeletonProps) {
	const [rootRef, size] = useElementSize<HTMLDivElement>()
	const showTicks = size.height === 0 || size.height >= MIN_HEIGHT_FOR_TICKS
	const tickKeys = useStableKeys(fitCount(size.width, TICK_SLOT_WIDTH, 2, 9))

	return (
		<SkeletonRegion
			ref={rootRef}
			label={label}
			className={cn('flex flex-col size-full min-h-0 overflow-hidden p-4 bg-background justify-between', className)}>
			{/* SVG Chart Area */}
			<div className='flex-1 relative w-full min-h-0'>
				<svg viewBox='0 0 500 200' preserveAspectRatio='none' className='size-full overflow-visible'>
					<title>Line chart skeleton</title>
					{/* Grid lines (horizontal) */}
					<line
						x1='30'
						y1='30'
						x2='470'
						y2='30'
						stroke='currentColor'
						className='text-muted-foreground/10'
						strokeDasharray='4 4'
					/>
					<line
						x1='30'
						y1='65'
						x2='470'
						y2='65'
						stroke='currentColor'
						className='text-muted-foreground/10'
						strokeDasharray='4 4'
					/>
					<line
						x1='30'
						y1='100'
						x2='470'
						y2='100'
						stroke='currentColor'
						className='text-muted-foreground/10'
						strokeDasharray='4 4'
					/>
					<line
						x1='30'
						y1='135'
						x2='470'
						y2='135'
						stroke='currentColor'
						className='text-muted-foreground/10'
						strokeDasharray='4 4'
					/>
					<line x1='30' y1='170' x2='470' y2='170' stroke='currentColor' className='text-muted-foreground/20' />

					{/* Y-Axis Line */}
					<line x1='30' y1='20' x2='30' y2='170' stroke='currentColor' className='text-muted-foreground/20' />

					{/* Spiky Line */}
					<path
						d={PATH_D}
						fill='none'
						stroke='currentColor'
						className='text-muted-foreground/35 animate-pulse'
						strokeWidth='3'
						strokeLinecap='round'
						strokeLinejoin='round'
					/>

					{/* Circular Markers */}
					{POINTS.map(p => (
						<circle
							key={p.id}
							cx={p.x}
							cy={p.y}
							r='6'
							fill='currentColor'
							className='text-muted-foreground/50 animate-pulse'
						/>
					))}
				</svg>
			</div>

			{/* X-Axis Labels (ticks) */}
			{showTicks && (
				<div className='flex justify-between pl-6 pr-4 mt-2 shrink-0'>
					{tickKeys.map(key => (
						<Skeleton key={key} className='h-3 w-10 bg-accent/50' />
					))}
				</div>
			)}
		</SkeletonRegion>
	)
}
